import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import ResizableTable from './ResizableTable';

/**
 * LogEventTable Component
 *
 * Displays a table of log events where each row is expandable to show detailed key-value information.
 * - Double-click a row to expand/collapse it.
 * - Collapsed rows truncate long messages.
 * - Expanded rows show key-value pairs (Timestamp, Level, Message, Count, First Seen) with color coding.
 * - Optimized for performance using memoization and React.memo.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.events - Array of log event objects to display
 * @param {number} props.lastUpdate - Timestamp of the last update to trigger re-renders
 * @param {number|string} props.height - Table height
 */
const DEFAULT_HEIGHT = 350;

const LOG_LEVEL_COLORS = {
  Debug:    { bg: 'rgba(102,102,102,0.09)', color: '#666' },
  Info:     { bg: 'rgba(49,120,198,0.09)', color: '#3178C6' },
  Warning:  { bg: 'rgba(245,158,11,0.13)', color: '#F59E0B' },
  Error:    { bg: 'rgba(220,38,38,0.13)', color: '#DC2626' },
  Critical: { bg: 'rgba(124,58,237,0.13)', color: '#7C3AED' },
  SegmentFault: { bg: 'rgba(255,0,255,0.13)', color: '#FF00FF' },
};

const LogEventTable = memo(({ events, lastUpdate, height }) => {
  /**
   * Format timestamp for display
   * 
   * @param {string} timestamp - ISO timestamp string
   * @returns {string} - Formatted date/time string
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return timestamp;
    }
  };
  
  // Sort events by timestamp (newest first)
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      if (!isNaN(timeA) && !isNaN(timeB)) {
        return timeB - timeA;
      }
      
      // Fallback to string comparison
      return String(b.timestamp).localeCompare(String(a.timestamp));
    });
  }, [events]);



  // Table columns definition
  const columns = [
    { key: 'timestamp', label: 'Timestamp', minWidth: 120, width: 160 },
    { key: 'level', label: 'Level', minWidth: 70, width: 90 },
    { key: 'count', label: 'Count', minWidth: 50, width: 70 },
    { key: 'message', label: 'Message', minWidth: 180, width: 350 },
    { key: 'first_seen', label: 'First Seen', minWidth: 120, width: 160 },
  ];

  /**
   * Renders a cell for the table.
   * @param {object} event
   * @param {string} colKey
   * @returns {JSX.Element|string}
   */
  const renderCell = (event, colKey) => {
    switch (colKey) {
      case 'timestamp':
        return formatTimestamp(event.timestamp);
      case 'level':
        return <span className={`level-badge level-${event.level.toLowerCase()}`}>{event.level}</span>;
      case 'count':
        return event.count || 1;
      case 'message':
        return <span className="message-cell">{event.message}</span>;
      case 'first_seen':
        return formatTimestamp(event.first_seen);
      default:
        return '';
    }
  };

  /**
   * Returns a class name for the row based on severity and archived status.
   * @param {object} event
   * @returns {string}
   */
  const getRowClass = (event) => {
    let className = '';
    if (event.level) className += `severity-${event.level}`;
    if (event.archived) className += ' log-event-archived';
    return className.trim();
  };

  /**
   * Renders the expanded key-value view for a log event.
   * @param {object} event
   * @returns {JSX.Element}
   */
  const expandableRowRender = (event) => {
    const color = LOG_LEVEL_COLORS[event.level] || LOG_LEVEL_COLORS.Info;
    return (
      <div
        className="log-event-expanded-details"
        style={{
          background: color.bg,
          borderLeft: `4px solid ${color.color}`,
          margin: '0 0 8px 0',
          padding: '12px 18px',
          borderRadius: 4,
          fontSize: '0.97em',
        }}
      >
        <div className="log-event-kv"><span className="log-event-k">Timestamp:</span> <span className="log-event-v">{formatTimestamp(event.timestamp)}</span></div>
        <div className="log-event-kv"><span className="log-event-k">Level:</span> <span className="log-event-v" style={{color: color.color}}>{event.level}</span></div>
        <div className="log-event-kv"><span className="log-event-k">Message:</span> <span className="log-event-v log-event-v-message">{event.message}</span></div>
        <div className="log-event-kv"><span className="log-event-k">Count:</span> <span className="log-event-v">{event.count || 1}</span></div>
        <div className="log-event-kv"><span className="log-event-k">First Seen:</span> <span className="log-event-v">{formatTimestamp(event.first_seen)}</span></div>
      </div>
    );
  };

  // Track expanded row key in local state
  const [expandedRowKey, setExpandedRowKey] = React.useState(null);

  // Use a stable key for each row (id if present, else timestamp+message)
  const getRowKey = (event, idx) => event.id || `${event.timestamp}_${event.message}`;

  // Handler for expansion
  const handleExpand = (rowKey, expanded) => {
    setExpandedRowKey(expanded ? rowKey : null);
  };

  return (
    <ResizableTable
      columns={columns}
      data={sortedEvents}
      height={height || DEFAULT_HEIGHT}
      renderCell={renderCell}
      getRowClass={getRowClass}
      expandableRowRender={expandableRowRender}
      getRowKey={getRowKey}
      expandedRowKey={expandedRowKey}
      onExpand={handleExpand}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Also check filters for shallow equality
  return (
    prevProps.events.length === nextProps.events.length &&
    prevProps.lastUpdate === nextProps.lastUpdate &&
    JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters)
  );
});

LogEventTable.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.string,
      level: PropTypes.string,
      message: PropTypes.string,
      count: PropTypes.number,
      first_seen: PropTypes.string,
      archived: PropTypes.bool
    })
  ).isRequired,
  filters: PropTypes.object,
  lastUpdate: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

LogEventTable.defaultProps = {
  lastUpdate: Date.now(),
  height: 350
};

export default LogEventTable;
